// dev_lp — reveal-on-scroll wrapper (fade + rise). WIX-style entrance.
// Fully respects the accessibility "no animations" preference and degrades
// safely: if animations are off or IntersectionObserver is unavailable,
// children render fully visible immediately.
import React, { useEffect, useRef, useState } from "react";
import { useShouldAnimate } from "../useShouldAnimate";

interface Props {
  children: React.ReactNode;
  delay?: number; // ms
  className?: string;
}

const Reveal: React.FC<Props> = ({ children, delay = 0, className = "" }) => {
  const shouldAnimate = useShouldAnimate();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!shouldAnimate || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [shouldAnimate]);

  if (!shouldAnimate) return <div className={className}>{children}</div>;

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
    >
      {children}
    </div>
  );
};

export default Reveal;
